import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube } from "lucide-react";

// TikTok has no lucide icon, so we ship a small brand-accurate SVG.
const TikTokIcon = ({ size = 15 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.6 6.7a5.6 5.6 0 0 1-3.3-1.1 5.5 5.5 0 0 1-2-3.5h-3.1v13.5a2.7 2.7 0 1 1-1.9-2.6v-3.2a5.9 5.9 0 1 0 5 5.8V9.3a8.6 8.6 0 0 0 5.3 1.8V8a5.6 5.6 0 0 1 0-1.3Z" />
  </svg>
);

const COLUMNS = [
  {
    title: "Shop",
    links: [
      { label: "Wedding Rings", to: "/shop/rings" },
      { label: "Ring Bands", to: "/shop/ring-bands" },
      { label: "Bracelets", to: "/shop/bracelets" },
      { label: "Necklaces", to: "/shop/necklaces" },
      { label: "Earrings", to: "/shop/earrings" },
      { label: "Shop All", to: "/shop" },
    ],
  },
  {
    title: "Customer Care",
    links: [
      { label: "Contact Us", to: "mailto:atelier@dreamonds.com" },
      { label: "Shipping & Returns", to: "/policies/shipping-returns" },
      { label: "Warranty", to: "/policies/shipping-returns" },
      { label: "Ring Resize", to: "/services/ring-resize" },
      { label: "FAQ", to: "/policies/shipping-returns" },
      { label: "Book Appointment", to: "/book/consultation" },
    ],
  },
  {
    title: "About",
    links: [
      { label: "Our Story", to: "/#story" },
      { label: "Sustainability", to: "/#sustainability" },
      { label: "Lab Grown Diamonds", to: "/#lab grown" },
      { label: "Blog", to: "/#blog" },
    ],
  },
];

export const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#0A0A0A] text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 md:gap-8">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-3 pr-4">
            <img
              src="/brand/dreamonds-logo-dark.png"
              alt="Dreamonds"
              className="h-12 w-auto -ml-2"
              data-testid="footer-logo"
            />
            <p className="mt-6 max-w-md text-white/50 leading-relaxed text-sm">
              Certified lab grown diamonds, designed in Canada. Every piece offered in
              yellow, white and rose gold. always priced in Canadian dollars.
            </p>

            <div className="mt-10 flex items-center gap-4">
              {[
                { Icon: Instagram, url: "https://instagram.com/dreamonds", key: "ig", label: "Instagram" },
                { Icon: Facebook, url: "https://facebook.com/dreamonds", key: "fb", label: "Facebook" },
                { Icon: Youtube, url: "https://youtube.com/@dreamonds", key: "yt", label: "YouTube" },
                { Icon: TikTokIcon, url: "https://tiktok.com/@dreamonds", key: "tt", label: "TikTok" },
              ].map(({ Icon, url, key, label }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${key}`}
                  className="w-9 h-9 flex items-center justify-center border border-white/20 hover:border-[#C8A165] hover:text-[#C8A165] transition-all"
                  aria-label={label}
                >
                  <Icon size={15} strokeWidth={1.4} />
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <p className="label-track text-[#C8A165] mb-5">{col.title}</p>
              <ul className="space-y-3 text-sm text-white/60">
                {col.links.map((l) => {
                  const isExternal = l.to.startsWith("http") || l.to.startsWith("mailto:");
                  const testid = `footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
                  return (
                    <li key={l.label}>
                      {isExternal ? (
                        <a href={l.to} className="hover:text-white transition-colors" data-testid={testid}>{l.label}</a>
                      ) : (
                        <Link to={l.to} className="hover:text-white transition-colors" data-testid={testid}>{l.label}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-16 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} Dreamonds. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/admin/login" className="hover:text-white transition-colors" data-testid="footer-admin-link">
              Studio Login
            </Link>
            <span>Designed in Canada · Prices in CAD</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
